import React from "react";
import { useNavigate } from "react-router-dom";
import { useSuiteHealth, type ServiceHealthTier } from "@weco/suite-chrome";
import { Button, PageHeader, ProductAppScaffold } from "@weco/ui";

function tierLabel(t: ServiceHealthTier): string {
  switch (t) {
    case "operational":
      return "Operational";
    case "degraded":
      return "Degraded";
    case "critical":
      return "Down";
    case "unreachable":
      return "Unreachable";
    default:
      return t;
  }
}

function tierBadgeStyle(t: ServiceHealthTier): React.CSSProperties {
  switch (t) {
    case "operational":
      return { background: "rgba(34, 197, 94, 0.15)", color: "#16A34A" };
    case "degraded":
      return { background: "rgba(245, 158, 11, 0.15)", color: "#D97706" };
    case "critical":
      return { background: "rgba(239, 68, 68, 0.15)", color: "#DC2626" };
    case "unreachable":
      return { background: "rgba(148, 163, 184, 0.2)", color: "#64748B" };
    default:
      return { background: "var(--bg-primary)", color: "var(--text-secondary)" };
  }
}

export const SuiteSystemStatusPage: React.FC = () => {
  const navigate = useNavigate();
  const { loading, services, lastRefreshed, error, refresh, footerLabel } = useSuiteHealth(60_000);

  const refreshedText =
    lastRefreshed !== null
      ? new Date(lastRefreshed).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "medium",
        })
      : "—";

  return (
    <ProductAppScaffold
      header={
        <PageHeader
          title="System status"
          subtitle={`Live checks against each suite API (${footerLabel})`}
          className="shrink-0 flex-wrap gap-4 py-4 px-4 sm:py-6 sm:px-8"
          actions={
            <div className="flex items-center gap-3">
              <Button type="button" variant="ghost" size="sm" onClick={() => navigate(-1)}>
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => void refresh()}
                disabled={loading}
                loading={loading}
              >
                {loading ? "Refreshing…" : "Refresh"}
              </Button>
            </div>
          }
        />
      }
      mainClassName="flex-1 min-h-0 overflow-y-auto"
    >
      <div className="max-w-3xl mx-auto p-6 md:p-10">
        {error && (
          <p className="text-sm mb-4" style={{ color: "#DC2626" }}>
            {error}
          </p>
        )}
        <p className="text-xs mb-6" style={{ color: "var(--text-muted)" }}>
          Last refreshed: {refreshedText}
        </p>

        {loading && services.length === 0 ? (
          <div
            className="rounded-xl border p-8 text-center text-sm"
            style={{
              borderColor: "var(--border-primary)",
              background: "var(--bg-primary)",
              color: "var(--text-muted)",
            }}
          >
            Loading service health…
          </div>
        ) : (
          <div
            className="rounded-xl border overflow-hidden"
            style={{
              borderColor: "var(--border-primary)",
              background: "var(--bg-primary)",
            }}
          >
            <table className="w-full text-left text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <th className="px-4 py-3 font-semibold" style={{ color: "var(--text-primary)" }}>
                    Service
                  </th>
                  <th className="px-4 py-3 font-semibold" style={{ color: "var(--text-primary)" }}>
                    Status
                  </th>
                  <th className="px-4 py-3 font-semibold" style={{ color: "var(--text-primary)" }}>
                    HTTP
                  </th>
                  <th className="px-4 py-3 font-semibold" style={{ color: "var(--text-primary)" }}>
                    Latency
                  </th>
                </tr>
              </thead>
              <tbody>
                {services.map((row) => (
                  <tr key={row.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium" style={{ color: "var(--text-primary)" }}>
                        {row.label}
                      </div>
                      {row.body?.checks && (
                        <details className="mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
                          <summary
                            className="cursor-pointer select-none"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            Health checks (JSON)
                          </summary>
                          <pre
                            className="mt-2 p-3 rounded-lg overflow-x-auto text-[11px] leading-relaxed max-w-md"
                            style={{
                              background: "var(--bg-root)",
                              border: "1px solid var(--border)",
                            }}
                          >
                            {JSON.stringify(row.body.checks, null, 2)}
                          </pre>
                        </details>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span
                        className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                        style={tierBadgeStyle(row.tier)}
                      >
                        {tierLabel(row.tier)}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top" style={{ color: "var(--text-secondary)" }}>
                      {row.httpStatus ?? "—"}
                    </td>
                    <td className="px-4 py-3 align-top" style={{ color: "var(--text-secondary)" }}>
                      {row.latencyMs !== null ? `${row.latencyMs} ms` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ProductAppScaffold>
  );
};
